import type { IEvent, IEventGroup } from '../services/api/eventApi';

export interface ReceiptPreselect {
  year?: string; // 년도 선택 칸 값 (예: '2026')
  selection?: string; // 이벤트/그룹 선택 칸 값 ('event:<id>' | 'group:<id>')
}

/**
 * /receipts?year=2026&event=<이벤트 또는 그룹 ID> 로 들어오면 년도와 이벤트/그룹 칸을 미리 고른다.
 * 영성수련회 안내 페이지(services/retreat-info)의 「참가비 영수증 발급」 버튼이 이렇게 연다.
 *
 * 선택 칸에 실제로 있는 값만 돌려준다 — 그룹에 묶인 이벤트는 개별 항목이 없으므로 그 그룹을,
 * 이벤트는 자기 년도에서만 보이므로 그 년도를 고른다. 모르는 ID 면 이벤트 칸은 비워 둔다.
 */
export const resolveReceiptPreselect = (
  search: string,
  events: IEvent[],
  groups: IEventGroup[],
): ReceiptPreselect => {
  const params = new URLSearchParams(search);
  const years = new Set(events.map((e) => String(e.event_Year)));
  const yearParam = (params.get('year') || '').trim();
  const year = /^\d{4}$/.test(yearParam) && years.has(yearParam) ? yearParam : undefined;
  const id = (params.get('event') || '').trim();

  const event = id ? events.find((e) => e._id === id) : undefined;
  if (event) {
    const owner = groups.find((g) => g.eventIds.includes(event._id));
    return {
      year: String(event.event_Year),
      selection: owner ? `group:${owner._id}` : `event:${event._id}`,
    };
  }

  const group = id ? groups.find((g) => g._id === id) : undefined;
  if (group) {
    const groupYears = events
      .filter((e) => group.eventIds.includes(e._id))
      .map((e) => String(e.event_Year))
      .sort();
    if (groupYears.length > 0) {
      return {
        year: year && groupYears.includes(year) ? year : groupYears[groupYears.length - 1],
        selection: `group:${group._id}`,
      };
    }
  }

  return year ? { year } : {};
};
